import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Clock, X, User, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { CRM_API, errMsg } from "./api";
import PageHeader from "./components/PageHeader";

const FILTERS = [
  { key: "due", label: "Active" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "snoozed", label: "Snoozed" },
  { key: "booked", label: "Booked" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
];

const STATUS_STYLE = {
  new: "bg-[#1F5EFF]/15 text-[#8db2ff] border-[#1F5EFF]/40",
  contacted: "bg-amber-400/10 text-amber-300 border-amber-400/40",
  snoozed: "bg-white/5 text-white/60 border-white/15",
  dismissed: "bg-white/5 text-white/35 border-white/10",
  booked: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40",
};

const BUCKET_STYLE = {
  overdue: "text-red-400",
  due_today: "text-amber-300",
  upcoming: "text-white/40",
};

function daysLabel(f) {
  if (f.effective_status === "snoozed") return `snoozed until ${f.snooze_until}`;
  const today = new Date().toLocaleDateString("en-CA");
  const diff = Math.round((new Date(f.due_at) - new Date(today)) / 864e5);
  if (diff < 0) return `${-diff} d overdue`;
  if (diff === 0) return "due today";
  return `in ${diff} d`;
}

export default function FollowUps() {
  const [filter, setFilter] = useState("due");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [customSnooze, setCustomSnooze] = useState(null); // fid being date-picked

  const load = useCallback(() => {
    CRM_API.get("/followups", { params: { filter } })
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(errMsg(e)));
    CRM_API.get("/followups/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (f, body, msg) => {
    try {
      await CRM_API.patch(`/followups/${f.id}`, body);
      toast.success(msg);
      setCustomSnooze(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="crm-followups">
      <PageHeader
        eyebrow="Customer retention"
        title="Follow-Ups."
        subtitle="Internal reminders only — nothing is sent to customers automatically. You decide when to reach out."
      />
      <div className="px-6 md:px-10 pb-16">
        {summary && (
          <div className="grid grid-cols-3 gap-3 mb-8" data-testid="followups-summary">
            <div className="p-4 rounded-xl bg-red-500/8 border border-red-500/25">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-red-300/70">Overdue</div>
              <div className="font-display text-3xl text-red-300 tabular-nums" data-testid="followups-overdue">{summary.overdue}</div>
            </div>
            <div className="p-4 rounded-xl bg-amber-400/8 border border-amber-400/25">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-amber-300/70">Due today</div>
              <div className="font-display text-3xl text-amber-300 tabular-nums" data-testid="followups-due-today">{summary.due_today}</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="text-[10px] font-mono-alt uppercase tracking-widest text-white/50">Upcoming 30 d</div>
              <div className="font-display text-3xl text-white tabular-nums" data-testid="followups-upcoming">{summary.upcoming}</div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="followups-filters">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`followups-filter-${f.key}`}
              className={`px-3.5 py-1.5 rounded-full text-xs border transition-colors ${
                filter === f.key
                  ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                  : "border-white/15 text-white/50 hover:border-white/30 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="p-10 text-center border border-dashed border-white/10 rounded-xl text-white/40 text-sm" data-testid="followups-empty">
            No reminders here. They appear automatically when a completed appointment matches a follow-up rule (CRM → Pricing).
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((f) => (
              <div key={f.id} data-testid={`followup-card-${f.id}`}
                className="p-4 md:p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Bell className="w-3.5 h-3.5 text-[#8db2ff] shrink-0" />
                      <span className="text-white font-medium text-sm" data-testid={`followup-suggested-${f.id}`}>
                        {f.suggested_service_name}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full border text-[9px] font-mono-alt uppercase tracking-widest ${STATUS_STYLE[f.effective_status || f.status]}`}
                        data-testid={`followup-status-${f.id}`}>
                        {f.effective_status || f.status}
                      </span>
                      {["new", "contacted"].includes(f.effective_status) && (
                        <span className={`text-[11px] font-mono-alt ${BUCKET_STYLE[f.bucket] || "text-white/40"}`}>
                          {daysLabel(f)}
                        </span>
                      )}
                      {f.effective_status === "snoozed" && (
                        <span className="text-[11px] font-mono-alt text-white/40">{daysLabel(f)}</span>
                      )}
                    </div>
                    <div className="mt-1.5 text-white/60 text-sm">
                      {f.customer_name || "Customer"}
                      {f.vehicle_label ? ` · ${f.vehicle_label}` : ""}
                    </div>
                    <div className="mt-1 text-white/40 text-xs">
                      {f.service_name} done {f.done_at} → suggest {f.suggested_service_name} · due {f.due_at}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/crm/customers/${f.customer_id}`} data-testid={`followup-view-${f.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-white/12 text-white/70 hover:text-white hover:bg-white/5">
                      <User className="w-3.5 h-3.5" /> View Customer
                    </Link>
                    {!["booked", "dismissed"].includes(f.status) && (
                      <>
                        <button onClick={() => act(f, { status: "contacted" }, "Marked as contacted")}
                          data-testid={`followup-contacted-${f.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-amber-400/40 text-amber-300 hover:bg-amber-400/10">
                          <Check className="w-3.5 h-3.5" /> Contacted
                        </button>
                        <button onClick={() => act(f, { snooze: "1w" }, "Snoozed 1 week")}
                          data-testid={`followup-snooze-1w-${f.id}`}
                          className="px-3 py-2 rounded-lg text-xs border border-white/12 text-white/60 hover:text-white hover:bg-white/5">
                          Snooze 1 wk
                        </button>
                        <button onClick={() => act(f, { snooze: "1m" }, "Snoozed 1 month")}
                          data-testid={`followup-snooze-1m-${f.id}`}
                          className="px-3 py-2 rounded-lg text-xs border border-white/12 text-white/60 hover:text-white hover:bg-white/5">
                          Snooze 1 mo
                        </button>
                        <button onClick={() => setCustomSnooze(customSnooze === f.id ? null : f.id)}
                          data-testid={`followup-snooze-custom-${f.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-white/12 text-white/60 hover:text-white hover:bg-white/5">
                          <CalendarClock className="w-3.5 h-3.5" /> Custom
                        </button>
                        <button onClick={() => act(f, { status: "dismissed" }, "Dismissed")}
                          data-testid={`followup-dismiss-${f.id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border border-red-400/30 text-red-300/80 hover:bg-red-500/10">
                          <X className="w-3.5 h-3.5" /> Dismiss
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {customSnooze === f.id && (
                  <div className="mt-3 flex items-center gap-2" data-testid={`followup-snooze-picker-${f.id}`}>
                    <input type="date" data-testid={`followup-snooze-date-${f.id}`}
                      min={new Date().toLocaleDateString("en-CA")}
                      className="bg-black/40 border border-white/12 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#1F5EFF]"
                      onKeyDown={(e) => { if (e.key === "Escape") setCustomSnooze(null); }}
                      onChange={(e) => e.target.value && act(f, { snooze: e.target.value }, `Snoozed until ${e.target.value}`)} />
                    <button onClick={() => setCustomSnooze(null)} className="text-white/40 text-xs hover:text-white">Cancel</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
